import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

interface PendingApplication {
  id: string;
  user_id: string;
  status: string;
  created_at: string | null;
}

export interface NewApplicant {
  id: string;
  fullName: string | null;
  nonce: number;
}

/**
 * Vigila las solicitudes de vendedor pendientes para administradores.
 * - `pendingCount`: cuántas solicitudes están en revisión ahora mismo.
 * - `newApplicant`: se rellena cuando llega una solicitud NUEVA mientras el
 *   admin está navegando (para lanzar la alerta). Se detecta comparando los
 *   IDs ya vistos con los actuales en cada sondeo.
 *
 * El sondeo se hace cada 30 segundos y no lanza alertas por las solicitudes
 * que ya existían al cargar la página.
 */
export function useSellerNotifications() {
  const { role } = useAuth();
  const isAdmin = role === "admin" || role === "super_admin";

  const [pendingCount, setPendingCount] = useState(0);
  const [newApplicant, setNewApplicant] = useState<NewApplicant | null>(null);

  const seenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const poll = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data, error } = await supabase
        .from("seller_applications")
        .select("id, user_id, status, created_at")
        .in("status", ["submitted", "under_review"])
        .order("created_at", { ascending: false });
      if (error || !data) return;

      const apps = data as PendingApplication[];
      setPendingCount(apps.length);

      const currentIds = new Set(apps.map((a) => a.id));

      // Primera carga: registramos lo que ya existe sin alertar.
      if (!initializedRef.current) {
        seenIdsRef.current = currentIds;
        initializedRef.current = true;
        return;
      }

      const fresh = apps.filter((a) => !seenIdsRef.current.has(a.id));
      seenIdsRef.current = currentIds;
      if (fresh.length === 0) return;

      // Buscamos el nombre del (o los) nuevo(s) solicitante(s).
      const userIds = [...new Set(fresh.map((a) => a.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);
      const nameById = new Map(
        (profiles ?? []).map((p) => [
          p.id as string,
          p.full_name as string | null,
        ])
      );

      const newest = fresh[0];
      setNewApplicant({
        id: newest.id,
        fullName: nameById.get(newest.user_id) ?? null,
        nonce: Date.now(),
      });
    } catch {
      // Silencioso: el sondeo no debe romper la navegación.
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    poll();
    const id = window.setInterval(poll, 30000);
    return () => window.clearInterval(id);
  }, [isAdmin, poll]);

  return {
    pendingCount,
    newApplicant,
    dismiss: () => setNewApplicant(null),
  };
}